import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import Notification from '../../../components/base/Notification';

interface TermDefinitionFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  editData?: any;
}

export default function TermDefinitionForm({ onClose, onSuccess, editData }: TermDefinitionFormProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHelp, setShowHelp] = useState<{ [key: string]: boolean }>({});
  const [formData, setFormData] = useState({
    term: '',
    general_definition: '',
    academic_definition: '',
    operational_definition: '',
    usage_context: '',
    measurement_method: '',
    source: '',
  });
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [researchTheme, setResearchTheme] = useState('');
  const [availableThemes, setAvailableThemes] = useState<string[]>([]);

  useEffect(() => {
    if (editData) {
      // 旧形式との互換性を保つ
      setFormData({
        term: editData.term || '',
        general_definition: editData.general_definition || editData.my_definition || '',
        academic_definition: editData.academic_definition || '',
        operational_definition: editData.operational_definition || '',
        usage_context: editData.usage_context || editData.usage_in_research || '',
        measurement_method: editData.measurement_method || '',
        source: editData.source || '',
      });
      setResearchTheme(editData.research_theme || '');
    }
    fetchAvailableThemes();
  }, [editData]);

  const fetchAvailableThemes = async () => {
    try {
      const { data, error } = await supabase
        .from('research_overview')
        .select('theme')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const themes = data?.map(item => item.theme).filter(Boolean) || [];
      setAvailableThemes(themes);
    } catch (error) {
      console.error('テーマ取得エラー:', error);
    }
  };

  const toggleHelp = (field: string) => {
    setShowHelp(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleDelete = async () => {
    if (!editData?.id) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('term_definitions')
        .delete()
        .eq('id', editData.id);

      if (error) throw error;

      setNotification({ type: 'success', message: '用語・定義を削除しました' });
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error('削除エラー:', error);
      setNotification({ type: 'error', message: '削除に失敗しました' });
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setNotification({ type: 'error', message: 'ログインが必要です。再度ログインしてください。' });
      return;
    }

    if (!formData.term || !formData.operational_definition) {
      setNotification({ type: 'error', message: '用語と本研究での操作的定義は必須です' });
      return;
    }

    setLoading(true);

    try {
      const dataToSave = {
        user_id: user.id,
        research_theme: researchTheme || null,
        ...formData,
      };

      if (editData?.id) {
        const { error } = await supabase
          .from('term_definitions')
          .update(dataToSave)
          .eq('id', editData.id);

        if (error) {
          console.error('UPDATE error:', error);
          throw new Error(`更新エラー: ${error.message}`);
        }
        setNotification({ type: 'success', message: '用語・定義を更新しました' });
      } else {
        const { error } = await supabase.from('term_definitions').insert(dataToSave);

        if (error) {
          console.error('INSERT error:', error);
          throw new Error(`保存エラー: ${error.message}`);
        }
        setNotification({ type: 'success', message: '用語・定義を保存しました' });
      }

      setTimeout(() => {
        if (onSuccess) onSuccess();
        else onClose();
      }, 1500);
    } catch (err: any) {
      console.error('保存エラー詳細:', err);
      setNotification({ type: 'error', message: `保存に失敗しました: ${err?.message || '不明なエラー'}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 md:p-8">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex items-center justify-between mb-5 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
          <i className="ri-book-mark-line text-green-600"></i>
          {editData ? '用語・定義を編集' : '用語・定義を追加'}
        </h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
          <i className="ri-close-line text-2xl"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
        {/* 用語セクション */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6 border-l-4 border-green-500">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-text text-green-600"></i>用語
          </h3>

          {/* 関連テーマ */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                関連テーマ
              </label>
            </div>
            <select
              value={researchTheme}
              onChange={(e) => setResearchTheme(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
            >
              <option value="">テーマを選択（任意）</option>
              {availableThemes.map((theme, index) => (
                <option key={index} value={theme}>{theme}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">「テーマと目的」で登録したテーマを選択できます</p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                用語 <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('term')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.term && (
              <div className="mb-3 p-3 bg-white border border-green-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p className="mb-1">研究で使う重要な専門用語や、独自の定義が必要な概念を記録します。</p>
                <p className="font-semibold mb-1">💡 記入例</p>
                <p>「事業承継税制」「実効税率」「親族内承継」</p>
              </div>
            )}

            <input
              type="text"
              value={formData.term}
              onChange={(e) => setFormData({ ...formData, term: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
              placeholder="例：事業承継税制"
              required
            />
          </div>
        </div>

        {/* 定義セクション */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 border-l-4 border-blue-500">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-book-open-line text-blue-600"></i>定義
          </h3>

          {/* 一般的な定義 */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                一般的な定義（教科書・辞書的）
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('general_definition')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.general_definition && (
              <div className="mb-3 p-3 bg-white border border-blue-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>教科書や辞書に載っているような、一般的な定義を記入します。</p>
              </div>
            )}

            <textarea
              value={formData.general_definition}
              onChange={(e) => setFormData({ ...formData, general_definition: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              rows={3}
              placeholder="〇〇とは、△△を指す制度/概念である"
            />
          </div>

          {/* 学術的な定義 */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                学術的な定義（先行研究での使われ方）
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('academic_definition')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.academic_definition && (
              <div className="mb-3 p-3 bg-white border border-blue-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>先行研究でこの用語がどのように定義され、使われているかを記入します。</p>
              </div>
            )}

            <textarea
              value={formData.academic_definition}
              onChange={(e) => setFormData({ ...formData, academic_definition: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              rows={3}
              placeholder="先行研究（著者, 年）では、〇〇を「△△」と定義している"
            />
          </div>

          {/* 本研究での操作的定義 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                本研究での操作的定義 <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('operational_definition')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.operational_definition && (
              <div className="mb-3 p-3 bg-white border border-blue-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>自分の研究でこの用語をどう定義し、どう測定するかを明確に記入します。</p>
              </div>
            )}

            <textarea
              value={formData.operational_definition}
              onChange={(e) => setFormData({ ...formData, operational_definition: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              rows={4}
              placeholder="本研究では、〇〇を「△△」と定義する。□□として測定する。"
              required
            />
          </div>
        </div>

        {/* 研究での使い方セクション */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 sm:p-6 border-l-4 border-purple-500">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-tools-line text-purple-600"></i>研究での使い方
          </h3>

          {/* どの文脈で使うか */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                どの文脈で使うか（変数として？概念として？）
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('usage_context')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.usage_context && (
              <div className="mb-3 p-3 bg-white border border-purple-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>この用語を研究のどの部分で、どのような役割で使うかを記入します。</p>
              </div>
            )}

            <textarea
              value={formData.usage_context}
              onChange={(e) => setFormData({ ...formData, usage_context: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
              rows={3}
              placeholder="〇〇変数として使用。△△を分析する際に□□として扱う。"
            />
          </div>

          {/* 具体的な測定方法・データ上の扱い */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                具体的な測定方法・データ上の扱い
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('measurement_method')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.measurement_method && (
              <div className="mb-3 p-3 bg-white border border-purple-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>データ上でこの用語をどう測定し、どのような値を取るかを具体的に記入します。</p>
              </div>
            )}

            <textarea
              value={formData.measurement_method}
              onChange={(e) => setFormData({ ...formData, measurement_method: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
              rows={3}
              placeholder="〇〇調査の「△△」という項目から作成。値の範囲は□□。"
            />
          </div>
        </div>

        {/* 定義の出典セクション */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 sm:p-6 border-l-4 border-amber-500">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-links-line text-amber-600"></i>定義の出典
          </h3>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                定義の出典（著者・年）
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('source')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.source && (
              <div className="mb-3 p-3 bg-white border border-amber-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>この定義を参考にした先行研究や文献の出典を記入します。</p>
              </div>
            )}

            <input
              type="text"
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
              placeholder="例：山田太郎 (2020)『事業承継の経済学』第3章"
            />
          </div>
        </div>

        {/* ボタン */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          {editData && (
            <button type="button" onClick={() => setShowDeleteConfirm(true)} className="w-full sm:w-auto px-6 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer">
              削除
            </button>
          )}
          <button type="button" onClick={onClose} className="w-full sm:flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer">
            キャンセル
          </button>
          <button type="submit" disabled={loading} className="w-full sm:flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer">
            {loading ? '保存中...' : editData ? '更新する' : '保存'}
          </button>
        </div>
      </form>

      {/* 削除確認モーダル */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-xl p-5 sm:p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-2">削除の確認</h3>
            <p className="text-slate-600 mb-6">この用語・定義を削除してもよろしいですか？この操作は取り消せません。</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="w-full sm:flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer">
                キャンセル
              </button>
              <button onClick={handleDelete} disabled={loading} className="w-full sm:flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer">
                {loading ? '削除中...' : '削除する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
